import type { RouteLocationRaw } from 'vue-router'
import { StorageKey } from '@/models/enums'
import useClipboard from 'vue-clipboard3'
import Toast from '@/components/common/popup/toast.vue'
import { i18n } from '@/i18n'
import dayjs from 'dayjs'
const $t = i18n.global.t

/**
 * 进入这一页时的地址 —— 模块加载即抓，早于 App.vue 的 setup、早于路由首次
 * 导航、也早于我们自己抹 URL 上 token 的那一步。
 *
 * 它就是小程序 `<web-view src>` 的值，微信 JS-SDK 的签名校验拿它作准。
 * 见下面 getSignUrl 的说明。
 */
const ENTRY_HREF = typeof window !== 'undefined' ? window.location.href : ''
const PI = 3.1415926535897932384626
const a = 6378245.0 //卫星椭球坐标投影到平面地图坐标系的投影因子。
const ee = 0.00669342162296594323 //椭球的偏心率。

/**
 * Formats the time into a string with minutes and seconds.
 *
 * @param {number} time - The time to be formatted in seconds.
 * @return {string} The formatted time string in the format "mm:ss".
 */
export const timerFormat = (time: number | undefined) => {
  if (!time) return ''
  let m = Math.floor(time / 60).toString()
  let s = (time % 60).toString()
  m = m.length == 1 ? '0' + m : m
  s = s.length == 1 ? '0' + s : s
  return `${m}:${s}`
}

export const weixinPriceFormat = (price: number) => {
  return toPrecision(price / 100, 2)
}

export const objToStr = Object.prototype.toString
export const toTypeStr = (val: unknown) => objToStr.call(val)

/**
 * 准确识别类型
 * @param {any} val 任何参数
 * @returns {string} 识别的类型
 */
export const identifyType = (val: unknown) => {
  return toTypeStr(val).replace('[object ', '').replace(']', '').toLowerCase()
}

/**
 * 空判断 - undefined null NaN '' '  ' {} [] 为空
 * @param {any} val 任何参数
 * @returns {boolean} 是否为空
 */
export function isEmpty(val: any) {
  // null or undefined
  if (val == null) return true

  if (typeof val === 'string' && val.trim() === '') return true

  if (typeof val === 'boolean') return false

  if (typeof val === 'number') return isNaN(val)

  if (val instanceof Error) return val.message === ''

  switch (identifyType(val)) {
    // Array
    case 'array':
      return !val.length

    // Plain Object
    case 'object': {
      return !Object.keys(val).length
    }

    // Map or Set or File
    case 'file':
    case 'map':
    case 'set': {
      return !val.size
    }
  }
}

/**
 * 去除浮点计算bug
 * @param num
 * @param precision 小数点后位数
 * @returns 结果
 */
export function toPrecision(num: string | number, precision = 0): number {
  if (isEmpty(num)) return Number(num)
  const result = Math.round(Number(num) * Math.pow(10, precision)) / Math.pow(10, precision)
  return parseFloat(result + '')
}

/**
 * 转金融数字
 * @param money
 * @param precision 小数点后位数
 * @param multiple 数字处理，如果要缩小100倍，则传入0.01即可
 * @return 转换后的金融数字
 */
export function toMoney(
  money: string | number | undefined | null,
  precision = 2,
  multiple?: number,
): string {
  if (isEmpty(money)) return ''

  if (multiple) money = Number(money) * multiple
  typeof money !== 'number' && (money = parseFloat(money as string))
  money = toPrecision(money, precision).toLocaleString()

  if (precision > 0) {
    const moneySplit = money.split('.')
    let decimal = moneySplit[1] || ''
    if (!decimal || decimal.length) {
      // 小数点位数补充
      while (decimal.length < precision) {
        decimal += '0'
      }
      money = `${moneySplit[0]}.${decimal}`
    }
  }

  return money
}

/**
 * Formats the given time in seconds into a human-readable format.
 *
 * @param {number} seconds - The time to be formatted in seconds.
 * @return {Object|string} An object with hours and minutes if the time is greater than or equal to 60 seconds, otherwise a string representing the time in minutes.
 */
export const formatTime = (seconds: number): { hours: number; minutes: number } => {
  if (seconds < 3600) {
    return {
      hours: 0,
      minutes: Math.floor(seconds / 60),
    }
  } else {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    return {
      hours,
      minutes,
    }
  }
}

/**
 * Generates a random GUID (Globally Unique Identifier) using the version 4 format.
 *
 * @return {string} The generated GUID.
 */
export const guid = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c == 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * Generates a random string of length 8 using characters from the given character set.
 *
 * @return {string} The randomly generated string.
 */
export const generateRandomString = () => {
  const chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'
  let randomString = ''

  for (let i = 0; i < 8; i++) {
    const randomIndex = Math.floor(Math.random() * chars.length)
    randomString += chars[randomIndex]
  }

  return randomString
}

export const countdown = (timerObject: any, clearStorage = false, key?: any) => {
  if (timerObject.timer) {
    clearTimeout(timerObject.timer)
  }
  timerObject.timer = setInterval(async () => {
    timerObject.totalTime -= 1
    if (timerObject.totalTime <= 0) {
      if (timerObject.timer) {
        clearTimeout(timerObject.timer)
        timerObject.timer = null
        timerObject.totalTime = 0
        if (clearStorage) {
          window.localStorage.removeItem(key)
        }
      }
    }
  }, 1000)
}

/**
 * 防抖（Debounce）：在事件被触发n秒后再执行回调，如果在这n秒内又被触发，则重新计时。
 * 适用场景：搜索框输入、按钮防重复点击、窗口resize等
 * 
 * 创建给定函数的防抖版本。
 *
 * @param {T} fn - 要进行防抖处理的函数。
 * @param {number} [delay=1000] - 防抖延迟时间，单位为毫秒。
 * @return {(...args: Parameters<T>) => void} - 防抖处理后的函数。
 */
export function useDebounce<T extends (...args: any[]) => void>(
  fn: T,
  delay: number = 1000,
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null

  return function (...args: Parameters<T>) {
    if (timeout) {
      clearTimeout(timeout)
    }

    timeout = setTimeout(() => {
      fn(...args)
      timeout = null
    }, delay)
  }
}

/**
 * 节流（Throttle）：规定在n秒内只能触发一次事件，无论期间触发多少次都只执行一次。
 * 适用场景：滚动事件、鼠标移动事件、API请求限制等
 * 
 * 创建给定函数的节流版本。
 *
 * @param {T} fn - 要进行节流处理的函数。
 * @param {number} [delay=1000] - 节流延迟时间，单位为毫秒。
 * @returns {(...args: Parameters<T>) => void} - 节流处理后的函数。
 */
export function useThrottle<T extends (...args: any[]) => void>(
  fn: T,
  delay: number = 1000,
): (...args: Parameters<T>) => void {
  let lastTime = 0

  return function (...args: Parameters<T>) {
    const now = +new Date()

    if (now - lastTime > delay) {
      fn(...args)
      lastTime = now
    }
  }
}

/**
 * Retrieves the URL parameters from the given URL string.
 *
 * @param {string} url - The URL string from which to extract the parameters.
 * @return {object} An object containing the URL parameters as key-value pairs.
 */
/**
 * 加固版：从给定 URL 字符串中检索所有 URL 参数。
 * 1. 支持自动解码 (decodeURIComponent)
 * 2. 自动忽略 hash (#) 之后的内容
 * 3. 兼容带 ? 的完整 URL 和纯参数字符串
 *
 * @param {string} url - 要从中提取参数的 URL 字符串。
 * @return {object} 包含键值对的参数对象。
 */
export const getUrlParams = (url: string): Record<string, string> => {
  const obj: Record<string, string> = {}
  if (!url) return obj

  try {
    // 兼容处理：提取查询字符串部分，并过滤掉 hash
    const queryString = url.includes('?') ? url.split('?')[1].split('#')[0] : url.split('#')[0]
    if (!queryString || !queryString.includes('=')) return obj

    const params = new URLSearchParams(queryString)
    params.forEach((value, key) => {
      obj[key] = value
    })
  } catch (e) {
    console.error('getUrlParams error:', e)
  }
  return obj
}

/**
 * 删除url参数
 * @param {array} params
 * @param {string} url
 * @return {string} 删除参数后的url
 */
export function deleteUrlParams(params: string[], url?: string): string {
  url = url || window.location.href
  const res = url.split('?')

  for (let i = 1; i < res.length; i++) {
    res[i] = `?${res[i]}`

    params.forEach((item) => {
      const reg = new RegExp(`[?&]${item}=([^&#]*)`, 'i')
      res[i] = res[i].replace(reg, '')
      res[i][0] === '&' && (res[i] = res[i].replace('&', '?'))
    })
  }

  return res.join('')
}

export const clearStorage = () => {
  storage.clearItem(StorageKey.MQTT_OPTION)
  storage.clearItem(StorageKey.DOCK_SN)
  storage.clearItem(StorageKey.DRONE_SN)
  storage.clearItem(StorageKey.ORDER_ID)
  storage.clearItem(StorageKey.FLIGHT_STATUS)
}

/**
 * Returns a promise that resolves after the specified number of milliseconds.
 *
 * @param {number} ms - The number of milliseconds to wait before resolving the promise.
 * @return {Promise<void>} A promise that resolves after the specified number of milliseconds.
 */
export const timeout = (ms: number) => {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

/**
 * Encodes the given object into a base64-encoded string.
 *
 * @param {object} data - The object to be encoded.
 * @return {string} The base64-encoded string representation of the object.
 */
export const encoding = (data: object) => {
  return window.btoa(JSON.stringify(data))
}

/**
 * Decodes a base64-encoded string into its original JSON format.
 *
 * @param {string} data - The base64-encoded string to decode.
 * @return {object} The decoded JSON object.
 */
export const decoding = (data: string) => {
  return JSON.parse(window.atob(data))
}

/**
 * Retrieves the WeChat code from the current URL's query parameters.
 *
 * @return {string} The WeChat code extracted from the URL.
 */
export const getWechatCode = () => {
  return window.location.search.substring(6).split('&')[0]
}

/**
 * Checks if the current device is running WeChat browser.
 *
 * @return {boolean} Returns true if the device is running WeChat browser, false otherwise.
 */
export const isWechatBrowser = () => {
  return /MicroMessenger/i.test(window.navigator.userAgent)
}

export const isMobileAndWeChatBrowser = () => {
  return isMobile() && isWechatBrowser()
}

export const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|OpenHarmony|Opera Mini/i.test(
    window.navigator.userAgent,
  )
}

/**
 * Checks if the current device is running iOS.
 *
 * @return {boolean} Returns true if the device is running iOS, false otherwise.
 */
export const isIOS = () => {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent)
}

/**
 * Checks if the current device is running on Android.
 *
 * @return {boolean} Returns true if the device is running on Android, false otherwise.
 */
export const isAndroid = () => {
  return /android/i.test(window.navigator.userAgent)
}

/**
 * Checks if the current device is running on OpenHarmony.
 *
 * @return {boolean} Returns true if the device is running on OpenHarmony, false otherwise.
 */
export const isHarmony = () => {
  return /OpenHarmony/i.test(window.navigator.userAgent)
}

/**
 * Checks if the array elements form a decreasing trend.
 *
 * @param {number[]} arr - The array to check for a decreasing trend.
 * @return {boolean} Returns true if the array elements form a decreasing trend, false otherwise.
 */
export const isDecreasingTrend = (arr: number[]) => {
  return arr.reduce((acc, curr, i, arr) => {
    if (i === 0) {
      return true
    }
    return acc && curr < arr[i - 1]
  }, true)
}

export const copyText = async (text: string) => {
  const { toClipboard } = useClipboard()
  try {
    await toClipboard(text)
    useToast({
      component: Toast,
      props: {
        content: $t('common.copySuccess'),
      },
      timeout: 2,
    })
  } catch (e) {
    useToast({
      component: Toast,
      props: {
        content: $t('common.copyFailed'),
      },
      timeout: 2,
    })
    console.error(e)
  }
}

export const formatNumber = (num: number) => {
  return num.toString().padStart(2, '0')
}

/**
 * 微信 JS-SDK 签名要用的 URL。
 *
 * 【2026-09-15 真机日志定的因果，改之前先读完】
 *
 * 症状：小程序 webview 里扫码偶发失败，一旦失败这个页面就一直失败，
 * 切到「我的」再切回来又好了。
 *
 * 离线日志里的因果链（Android 16 / 微信 8.0.77 / test 站）：
 *
 *   请求 /auth/wx/mpConfigInfo 的 body：
 *     url = https://mss2test.aklmss.com/home?t=...&locale=zh-CN&userInfo={...}
 *   而 PageSpy 记的页面入口地址：
 *     https://mss2test.aklmss.com/?t=...&locale=zh-CN&userInfo={...}
 *                                ↑ 根路径，不是 /home
 *   结果：
 *     微信JS-SDK配置失败  config:invalid signature
 *     扫码失败           scanQRCode:permission denied
 *
 * 也就是说：**我们拿路由跳转之后的地址去签名，而微信在小程序 webview 里
 * 校验的是 `<web-view src>` 那个原始地址** —— 页面内的 history/SPA 路由
 * 改不动它。签名当然对不上，整页 JS-SDK 随之全部 permission denied。
 *
 * 偶发也解释得通：config 撞在路由把 `/` 改写成 `/home` 之前发出去，
 * 那一次就签对了、就能用；撞在之后就废。
 *
 * 所以：
 *   · 小程序 webview（任意系统）→ 一律用**进入这一页时**的地址
 *   · iOS 微信浏览器 → 本来就要用进入时的地址（微信的老规矩）
 *   · 其余（安卓微信浏览器等）→ 维持用当前地址，那边微信是跟着
 *     pushState 走的，换成入口地址反而会错
 *
 * ⚠️ 用 ENTRY_HREF 而不是 storage 里的 WX_CODE_URL：后者是 App.vue 的
 * onMounted 写的，而子组件的 onActivated 比父级 onMounted 早 —— 首屏那次
 * config 完全可能读到空值。ENTRY_HREF 在模块加载时就抓好了，早于一切。
 */
export const getSignUrl = () => {
  const ua = navigator.userAgent.toLowerCase()
  // UA 里的 miniprogram 标记：微信小程序 webview 是 `miniProgram/<appid>`
  if (/miniprogram/.test(ua)) return ENTRY_HREF
  if (/iphone|ipad|ipod/.test(ua)) return storage.getItem(StorageKey.WX_CODE_URL) || ENTRY_HREF
  return window.location.href
}

export function separateIntegerAndDecimal(number: number) {
  const numStr = number.toString()
  const decimalIndex = numStr.indexOf('.')
  if (decimalIndex === -1) {
    return {
      integer: numStr,
      decimal: null,
    }
  } else {
    return {
      integer: numStr.substring(0, decimalIndex),
      decimal: '.' + numStr.substring(decimalIndex + 1),
    }
  }
}

//转化经度
function transformlng(lng: number, lat: number) {
  lng = Number(lng)
  lat = Number(lat)
  let ret =
    300.0 + lng + 2.0 * lat + 0.1 * lng * lng + 0.1 * lng * lat + 0.1 * Math.sqrt(Math.abs(lng))
  ret += ((20.0 * Math.sin(6.0 * lng * PI) + 20.0 * Math.sin(2.0 * lng * PI)) * 2.0) / 3.0
  ret += ((20.0 * Math.sin(lng * PI) + 40.0 * Math.sin((lng / 3.0) * PI)) * 2.0) / 3.0
  ret += ((150.0 * Math.sin((lng / 12.0) * PI) + 300.0 * Math.sin((lng / 30.0) * PI)) * 2.0) / 3.0
  return ret
}

//转化纬度
function transformlat(lng: number, lat: number) {
  lng = Number(lng)
  lat = Number(lat)
  let ret =
    -100.0 +
    2.0 * lng +
    3.0 * lat +
    0.2 * lat * lat +
    0.1 * lng * lat +
    0.2 * Math.sqrt(Math.abs(lng))
  ret += ((20.0 * Math.sin(6.0 * lng * PI) + 20.0 * Math.sin(2.0 * lng * PI)) * 2.0) / 3.0
  ret += ((20.0 * Math.sin(lat * PI) + 40.0 * Math.sin((lat / 3.0) * PI)) * 2.0) / 3.0
  ret += ((160.0 * Math.sin((lat / 12.0) * PI) + 320 * Math.sin((lat * PI) / 30.0)) * 2.0) / 3.0
  return ret
}
//判断时候在国内还是国外
function out_of_china(lon: number, lat: number) {
  lon = Number(lon)
  lat = Number(lat)
  if (lon < 72.004 || lon > 137.8347) {
    return true
  }
  if (lat < 0.8293 || lat > 55.8271) {
    return true
  }
  return false
}
//wgs84 to gcj02   地球坐标系 转 火星坐标系
/**
 * 两点直线距离（米）。Haversine，地球取平均半径 6371008.8m。
 *
 * 打卡点列表接口只回 lon / lat，没有 distance 字段；而用户坐标在 userStore
 * 里本来就有 —— 所以「近多少」是客户端算得出来的，不需要多发一个请求。
 *
 * 直线距离不是驾车距离，会比实际路程短。用在「哪个更近」这种相对判断上
 * 足够；要给用户看精确路程得走高德算路（m-nav-btn 那条路，有成本）。
 */
export const distanceInMeters = (
  from: [number, number],
  to: [number, number],
) => {
  const R = 6371008.8
  const toRad = (d: number) => (d * PI) / 180
  const [lon1, lat1] = from
  const [lon2, lat2] = to
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)))
}

/**
 * 距离的口语化写法：1km 以下按十米取整说「800m」，以上说「12.3km」。
 * 只保留一位小数 —— 这是个「值不值得换」的量级判断，不是导航。
 */
export const formatDistance = (meters: number) => {
  if (!Number.isFinite(meters) || meters <= 0) return ''
  if (meters < 1000) return `${Math.round(meters / 10) * 10}m`
  return `${(meters / 1000).toFixed(1).replace(/\.0$/, '')}km`
}

export function wgs84togcj02(lng: string | number, lat: string | number) {
  lng = Number(lng)
  lat = Number(lat)
  let dlat = transformlat(lng - 105.0, lat - 35.0)
  let dlng = transformlng(lng - 105.0, lat - 35.0)
  const radlat = (lat / 180.0) * PI
  let magic = Math.sin(radlat)
  magic = 1 - ee * magic * magic
  const sqrtmagic = Math.sqrt(magic)
  dlat = (dlat * 180.0) / (((a * (1 - ee)) / (magic * sqrtmagic)) * PI)
  dlng = (dlng * 180.0) / ((a / sqrtmagic) * Math.cos(radlat) * PI)
  const mglat = lat + dlat
  const mglng = lng + dlng

  return [mglng, mglat]
}

//gcj02 to wgs84  火星坐标系 转 地球坐标系
export function gcj02towgs84(lng: number, lat: number) {
  lng = Number(lng)
  lat = Number(lat)
  const originalLngSign = Math.sign(lng)
  const originalLatSign = Math.sign(lat)
  lat = Math.abs(lat)
  lng = Math.abs(lng)
  let dlat = transformlat(lng - 105.0, lat - 35.0)
  let dlng = transformlng(lng - 105.0, lat - 35.0)
  const radlat = (lat / 180.0) * PI
  let magic = Math.sin(radlat)
  magic = 1 - ee * magic * magic
  const sqrtmagic = Math.sqrt(magic)
  dlat = (dlat * 180.0) / (((a * (1 - ee)) / (magic * sqrtmagic)) * PI)
  dlng = (dlng * 180.0) / ((a / sqrtmagic) * Math.cos(radlat) * PI)
  const mglat = lat + dlat
  const mglng = lng + dlng
  const lngs = lng * 2 - mglng
  const lats = lat * 2 - mglat
  const finalLng = originalLngSign * lngs
  const finalLat = originalLatSign * lats

  return [finalLng, finalLat]
}

/**
 * https://blog.grossman.io/how-to-write-async-await-without-try-catch-blocks-in-javascript/
 * @param {promise} promise
 * @param {object} errorExt 你可以传递给 err 对象的附加信息
 * @return {promise}
 */
export function to<T, U = Error>(
  promise: Promise<T>,
  errorExt?: object,
): Promise<[U, undefined] | [null, T]> {
  return promise
    .then<[null, T]>((data: T) => [null, data])
    .catch<[U, undefined]>((err: U) => {
      if (errorExt) {
        const parsedError = Object.assign({}, err, errorExt)
        return [parsedError, undefined]
      }

      return [err, undefined]
    })
}

/**
 * 通过文件名称判断是否属于 suffixs 中的类型
 * @param name 文件名称
 * @param suffixs 类型数组（如：['jpg', 'png']）
 * @returns 是否属于 suffixs 中的类型
 */
export function isTypeByName(name: string, suffixs: Array<string>) {
  if (!name) return false

  const nameSplit = name.split('.')
  const suffix = nameSplit[nameSplit.length - 1].toLowerCase()

  return suffixs.indexOf(suffix) > -1
}

/**
 * 图片文件类型
 */
export const imgType = [
  'xbm',
  'tif',
  'pjp',
  'svgz',
  'jpg',
  'jpeg',
  'ico',
  'tiff',
  'gif',
  'svg',
  'jfif',
  'webp',
  'png',
  'bmp',
  'pjpeg',
  'avif',
]

/**
 * 媒体文件类型
 */
export const mediaType = [
  'mpg',
  'mpeg',
  'avi',
  'rm',
  'rmvb',
  'mov',
  'wmv',
  'asf',
  'dat',
  'asx',
  'wvx',
  'mpe',
  'mpa',
  'mp4',
]

/**
 * 是否为图片文件类型
 * @param name 文件名称
 * @returns 是否为图片文件类型
 */
export function isImgByName(name: string) {
  return isTypeByName(name, imgType)
}

/**
 * 是否为媒体文件类型
 * @param name 文件名称
 * @returns 是否为媒体文件类型
 */
export function isMediaByName(name: string) {
  return isTypeByName(name, mediaType)
}

/**
 * 通过 shell 进行 replace 或 push
 * @param to 路由參數
 */
export function routerReplaceByShell(to: RouteLocationRaw) {
  if (useGlobalStore().inMP) {
    return router.push(to)
  } else {
    return router.replace(to)
  }
}

/**
 * 获取 url 指定参数
 * @param key 参数字段
 * @param url 地址（默认当前地址）
 * @returns 指定参数
 */
export function getUrlParam(key: string, url = window.location.href) {
  const keyRegx = new RegExp('(^|\\?|&|#)(?:' + key + ')=([^&#]*)(&|$)?')
  const keyRegxResult = url.match(keyRegx)
  return keyRegxResult ? decodeURIComponent(keyRegxResult[2] || '') || '' : ''
}

/**
 * 脱敏
 * @param {string} str 值
 * @param {number} start 开始位置
 * @param {number} end 结束位置
 * @param {string|number} replacement 替换符合
 * @returns {string} 脱敏后的值
 */
export function desensitization(
  str: string,
  start: number,
  end: number,
  replacement: string | number = '*',
) {
  if (!str || str.length < Number(end)) return str

  const startStr = str.slice(0, start)
  const endStr = str.slice(end)
  let res = startStr

  const fillingNum = end - start
  for (let i = 0; i < fillingNum; i++) {
    res += replacement
  }
  res += endStr

  return res
}

/**
 * 简单复制
 * @param data 要复制的数组、对象
 * @returns 深度复制出的新对象
 */
export function easyCopy<T>(data: T): T {
  return JSON.parse(JSON.stringify(data))
}

/**
 * 判断对象、数组是否有某属性
 * @param val
 * @param key
 * @returns 是否含有
 */
export function hasOwn(
  val: Record<string, any>,
  key: string | number | symbol,
): key is keyof typeof val {
  return Object.prototype.hasOwnProperty.call(val, key)
}

/**
 * 空闲时执行
 * @param cb 空闲帧
 * @param frameTimeLimit 帧时间限制
 * @return requestAnimationFrame
 */
export function executeWhenIdle(cb: (frameTime: number) => void, frameTimeLimit = 15) {
  const startTime = performance.now()
  return requestAnimationFrame((timestamp) => {
    const frameTime = timestamp - startTime
    if (frameTime <= frameTimeLimit) {
      setTimeout(() => {
        cb(frameTime)
      })
    } else {
      console.log('帧忙碌', frameTime)
      requestAnimationFrame(() => {
        executeWhenIdle(cb)
      })
    }
  })
}

export function getRealPx(px: number) {
  return toPrecision((px / 10) * parseFloat(document.documentElement.style.fontSize), 2)
}

/**
 * 对比 oss 地址，没过期的话使用旧地址
 * @param newUrl
 * @param oldUrl
 * @returns
 */
export function ossUrlComparison<T = string | undefined | null>(
  newUrl: T,
  oldUrl: string | undefined | null,
) {
  if (!oldUrl || !newUrl) return newUrl

  if ((newUrl as string).split('?')[0] !== oldUrl.split('?')[0]) return newUrl

  const oldExpires = getUrlParam('Expires', oldUrl)
  if (!oldExpires) return newUrl

  const now = +new Date() / 1000
  const oldNumExpires = Number(oldExpires)
  if (oldNumExpires - now > 60 * 5) return oldUrl

  return newUrl
}

/**
 * 中止包装器
 * @param {promise} p1
 * @return {promise}
 */
export interface AbortWrapperPromise<T = any> extends Promise<T> {
  abort?: (reasonToAbort: any) => void
}
export function abortWrapper<T>(p1: Promise<T>) {
  let abort
  const p2 = new Promise((resolve, reject) => (abort = reject))
  const p = Promise.race([p1, p2]) as AbortWrapperPromise<T>
  p.abort = abort
  return p
}

export function currencyFormat(currency?: string) {
  const map = {
    'CNY': '¥',
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'JPY': '¥',
    'TWD': 'NT$',
    'HKD': 'HK$',
    'MOP': 'MOP$',
    'AUD': 'A$'
  }
  return currency ? map[currency as keyof typeof map] : '¥'
}

/**
 * 下载文件工具函数
 * @param fileDownloadUrl 文件下载地址
 * @param fileName 文件名称
 * @description 兼容手机和电脑浏览器的文件下载功能
 */
export const downloadUrl = async (fileDownloadUrl: string, fileName?: string) => {
  try {
    // 检测是否为移动设备
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

    // 获取文件名
    const finalFileName = fileName || fileDownloadUrl.split('/').pop()?.split('?')[0] || '未知文件'
    // 移动端处理
    if (isMobile) {
      try {
        // 移动端也使用fetch获取二进制流下载
        const response = await fetch(fileDownloadUrl, {
          mode: 'cors'
        })

        if (!response.ok) {
          throw new Error(`HTTP错误! 状态: ${response.status}`)
        }

        // 获取文件blob
        const blob = await response.blob()

        // 创建下载链接
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = finalFileName
        link.target = '_self'
        link.rel = 'noopener noreferrer'

        // 添加到DOM并触发下载
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        // 清理资源
        window.URL.revokeObjectURL(url)

      } catch (mobileError) {
        // 如果移动端fetch失败，使用备用方案
        console.warn('移动端fetch下载失败，使用备用方案:', mobileError)

        const link = document.createElement('a')
        link.href = fileDownloadUrl
        link.target = '_self'
        link.rel = 'noopener noreferrer'

        // 尝试触发下载
        if ('download' in link) {
          link.download = finalFileName
        }

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }

      return
    }

    // 桌面端处理
    try {
      // 首先尝试直接下载（适用于同域或支持CORS的资源）
      const response = await fetch(fileDownloadUrl, {
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP错误! 状态: ${response.status}`)
      }

      // 获取文件blob
      const blob = await response.blob()

      // 创建下载链接
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = finalFileName

      // 隐藏链接并添加到DOM
      link.style.display = 'none'
      document.body.appendChild(link)

      // 触发下载
      link.click()

      // 延迟清理资源，确保下载开始
      setTimeout(() => {
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }, 100)

    } catch (fetchError) {
      // 如果fetch失败（如跨域问题），则使用备用方案
      console.warn('Fetch下载失败，使用备用方案:', fetchError)

      const link = document.createElement('a')
      link.href = fileDownloadUrl
      link.download = finalFileName
      link.target = '_blank'
      link.rel = 'noopener noreferrer'

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

  } catch (error) {
    console.error('下载错误:', error)

    // 最终备用方案：直接打开链接
    try {
      window.open(fileDownloadUrl, '_blank')
    } catch (openError) {
      console.error('打开链接也失败:', openError)
      alert('下载失败，请手动复制链接下载')
    }
  }
}

/**
 * 格式化发布时间为相对时间显示
 * @param timestamp 时间戳（字符串或Date对象）
 * @returns 格式化后的时间字符串
 */
export const formatPublishTime = (timestamp: string | Date): string => {
  if (!timestamp) return ''

  const date = dayjs(timestamp)
  const now = dayjs()
  const diffInMinutes = now.diff(date, 'minute')
  const diffInHours = now.diff(date, 'hour')
  const diffInDays = now.diff(date, 'day')

  // 相对时间显示
  const isEn = i18n.global.locale.value === 'en-US'
  if (diffInMinutes < 1) {
    return $t('common.just')
  } else if (diffInMinutes < 60) {
    return isEn
      ? `${diffInMinutes} ${$t('common.minute')} ${$t('common.ago')}`
      : `${diffInMinutes}${$t('common.minute')}${$t('common.ago')}`
  } else if (diffInHours < 24) {
    return isEn
      ? `${diffInHours} ${$t('common.hour')} ${$t('common.ago')}`
      : `${diffInHours}${$t('common.hour')}${$t('common.ago')}`
  } else if (diffInDays < 7) {
    return isEn
      ? `${diffInDays} ${$t('common.day')} ${$t('common.ago')}`
      : `${diffInDays}${$t('common.day')}${$t('common.ago')}`
  } else {
    return date.format('YYYY/MM/DD HH:mm')
  }
}

/**
 * 将base64编码的字符串解码为普通字符串
 * @param base64Str base64格式的字符串
 * @returns 解码后的正常文字
 */
export const decodeBase64Text = (base64Str: string): string => {
  if (!base64Str) return ''
  if (typeof base64Str !== 'string') {
    console.error('Base64解码失败: 输入不是有效字符串')
    return ''
  }

  // 处理非标准base64字符串，移除可能存在的换行和填充
  let sanitizedStr = base64Str.replace(/[\r\n\s]/g, '')
  // 填充=到长度为4的倍数
  if (sanitizedStr.length % 4 !== 0) {
    sanitizedStr += '='.repeat(4 - (sanitizedStr.length % 4))
  }

  try {
    // atob 会抛异常，如果不是合法base64
    const binaryStr = atob(sanitizedStr)
    try {
      // 再将二进制字符串转为utf-8字符串，兼容中文
      const utf8Str = decodeURIComponent(
        Array.prototype.map
          .call(binaryStr, (c: string) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
      return utf8Str
    } catch (decodeErr) {
      // 如果URI解码失败可能是非UTF-8编码
      return binaryStr
    }
  } catch (e) {
    console.error('Base64解码失败:', e)
    return ''
  }
}

export const handleMiniProgramUrl = (url: string, actionKey: string) => {
  console.log('URL:', url)
  console.log('Action Key:', actionKey)

  // 如果不是正常的URL（不包含miniprogram），直接返回原URL
  if (!url.includes('miniprogram') || !url.includes('?')) return url

  const paramsObj = getUrlParams(url)
  if (isEmpty(paramsObj)) return url

  const { action, params } = paramsObj
  if (action !== actionKey) return url

  try {
    return JSON.parse(decodeURIComponent(params))
  } catch (e) {
    console.error('解析小程序参数失败:', e)
    return url
  }
}

/**
 * 从OSS下载JSON数据并解析为对象
 * @param {string} url JSON文件的URL
 * @return {Promise<any>} 解析后的JSON对象
 */
export async function downloadOssJsonAsObject(url: string): Promise<any> {
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const jsonData = await response.json()
    return jsonData
  } catch (error) {
    console.error('下载OSS JSON数据失败:', error)
    throw error
  }
}

export const dataUrlToFile = (dataUrl: string, filename: string) => {
  const [header, data] = dataUrl.split(',')
  if (!header || !data) return null
  const match = header.match(/data:(.*);base64/)
  const mime = match?.[1] || 'image/png'
  const binary = atob(data)
  const len = binary.length
  const u8arr = new Uint8Array(len)
  for (let i = 0; i < len; i++) u8arr[i] = binary.charCodeAt(i)
  return new File([u8arr], filename, { type: mime })
}